/**
 * PermissionContext — Provides user permissions to all components.
 * Fetches permissions from backend on login, caches in state.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toArrayPayload } from '../utils/formatters';

interface PermissionContextType {
  permissions: string[];
  role: string | null;
  doctorStatus: string | null;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  isDoctorApproved: boolean;
  isDoctorPending: boolean;
  isDoctorRejected: boolean;
  loading: boolean;
}

const PermissionContext = createContext<PermissionContextType>({
  permissions: [],
  role: null,
  doctorStatus: null,
  hasPermission: () => false,
  hasAnyPermission: () => false,
  hasAllPermissions: () => false,
  isDoctorApproved: false,
  isDoctorPending: false,
  isDoctorRejected: false,
  loading: true,
});

export const usePermissions = () => useContext(PermissionContext);

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [permissions, setPermissions] = useState<string[]>([]);
  const [role, setRole] = useState<string | null>(null);
  const [doctorStatus, setDoctorStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const API_BASE = process.env.REACT_APP_API_URL || 'https://health.bhabygroup.co.tz/api';
      const res = await fetch(`${API_BASE}/users/my-permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setPermissions(toArrayPayload(data.permissions));
        setRole(data.role || null);
        setDoctorStatus(data.doctorStatus || null);
      }
    } catch {
      // Silent — will use cached/default permissions
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Re-fetch when token changes (login/logout)
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'token') {
        fetchPermissions();
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [fetchPermissions]);

  const hasPermission = useCallback((permission: string) => {
    return permissions.includes(permission);
  }, [permissions]);

  const hasAnyPermission = useCallback((perms: string[]) => {
    return perms.some(p => permissions.includes(p));
  }, [permissions]);

  const hasAllPermissions = useCallback((perms: string[]) => {
    return perms.every(p => permissions.includes(p));
  }, [permissions]);

  const isDoctorApproved = doctorStatus === 'approved';
  const isDoctorPending = ['pending_review', 'pending_documents', 'draft', 'none'].includes(doctorStatus || '');
  const isDoctorRejected = doctorStatus === 'rejected';

  return (
    <PermissionContext.Provider
      value={{
        permissions,
        role,
        doctorStatus,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        isDoctorApproved,
        isDoctorPending,
        isDoctorRejected,
        loading,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};
